import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/client.js';
import {
  fetchPresentationViewBlob,
  useDeletePresentation,
  useMintPresentationShare,
  usePresentation,
  usePresentations,
  usePresentationShares,
  useRevokePresentationShare,
  type PresentationSummary,
} from '../api/presentations.js';
import { UNTRUSTED_FRAME_SANDBOX_WITH_MODALS } from '../lib/untrusted-frame.js';

/**
 * PresentationsPage — the third authoring peer alongside Reports and
 * Dashboards. A presentation is a self-contained HTML deck; this page
 * lists them (left rail) and renders the selected deck's current
 * version in an iframe (right), with print + share-link management.
 *
 * The viewer fetches the authenticated `/presentations/:id/view` HTML
 * with the bearer token and shows it via a Blob URL — an iframe `src`
 * can't carry an Authorization header, so the blob hop is required.
 * Public sharing is delegated to the kiosk endpoint via minted tokens
 * (rendered at /p/:id/:token, no auth).
 *
 * Role gates live on the API. We don't read roles from the token here
 * (the prod JWT isn't parseable client-side), so Share/Delete are shown
 * and a 403 is surfaced as a friendly message instead of being hidden.
 */
export function PresentationsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const {
    data: list,
    isLoading,
    error,
  } = usePresentations(search.trim() ? { query: search.trim() } : undefined);

  return (
    <div className="pres-page">
      <aside className="pres-list">
        <div className="pres-list-head">
          <input
            type="search"
            className="pres-search"
            placeholder="Search presentations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => navigate('/presentations/new')}
            title="Create a new presentation"
          >
            + New
          </button>
        </div>
        <div className="pres-list-scroll">
          {isLoading && <div className="pres-list-note">Loading…</div>}
          {error && (
            <div className="pres-list-note pres-list-note--err">{(error as Error).message}</div>
          )}
          {list && list.length === 0 && (
            <div className="pres-list-note">{search ? 'No matches.' : 'No presentations yet.'}</div>
          )}
          {list?.map((p) => (
            <PresentationRow
              key={p.id}
              p={p}
              active={p.id === id}
              onClick={() => navigate(`/presentations/${p.id}`)}
            />
          ))}
        </div>
      </aside>

      <section className="pres-detail">
        {id ? (
          <PresentationDetail key={id} id={id} onDeleted={() => navigate('/presentations')} />
        ) : (
          <EmptyDetail hasAny={!!list && list.length > 0} />
        )}
      </section>

      <PresStyles />
    </div>
  );
}

function PresentationRow({
  p,
  active,
  onClick,
}: {
  p: PresentationSummary;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={'pres-row' + (active ? ' pres-row--active' : '')}
      onClick={onClick}
    >
      <span className="pres-row-name">{p.name}</span>
      <span className="pres-row-meta">
        <span className={'badge badge--' + audienceBadge(p.audience)}>{p.audience}</span>
        {!p.currentVersionId && <span className="badge badge--warn">draft</span>}
        <span className="pres-row-date">{formatRelative(p.updatedAt)}</span>
      </span>
    </button>
  );
}

function EmptyDetail({ hasAny }: { hasAny: boolean }): JSX.Element {
  return (
    <div className="pres-empty">
      <div className="pres-empty-icon" aria-hidden="true">
        ▤
      </div>
      <h2 className="pres-empty-title">
        {hasAny ? 'Select a presentation' : 'No presentations yet'}
      </h2>
      <p className="pres-empty-sub">
        {hasAny
          ? 'Pick a deck on the left to view it, print it, or create a shareable link.'
          : 'Presentations authored into this tenant will appear here.'}
      </p>
    </div>
  );
}

function PresentationDetail({ id, onDeleted }: { id: string; onDeleted: () => void }): JSX.Element {
  const api = useApi();
  const { data: meta } = usePresentation(id);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(true);
  const [viewError, setViewError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Fetch the authed HTML and wrap it in a blob URL the iframe can load.
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setViewLoading(true);
    setViewError(null);
    setBlobUrl(null);
    fetchPresentationViewBlob(api.fetchRaw.bind(api), id)
      .then((u) => {
        if (cancelled) {
          try {
            URL.revokeObjectURL(u);
          } catch {
            /* noop */
          }
          return;
        }
        url = u;
        setBlobUrl(u);
      })
      .catch((e) => {
        if (!cancelled) setViewError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setViewLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* noop */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const del = useDeletePresentation();
  function handleDelete(): void {
    const name = meta?.name ?? 'this presentation';
    if (
      !window.confirm(
        `Delete "${name}"? This removes the presentation and every published version. This can't be undone.`,
      )
    )
      return;
    del.mutate(id, {
      onSuccess: onDeleted,
      onError: (e) => window.alert(`Delete failed: ${(e as Error).message}`),
    });
  }

  function handlePrint(): void {
    // CSAGE-310: the deck iframe is opaque-origin (no allow-same-origin),
    // so we can no longer drive it by calling .print() as if it were our
    // own document. The API-injected print bridge (see
    // injectPresentationBridge in presentation.controller.ts) listens for
    // this message on every presentation response and calls
    // window.print() from inside the frame itself.
    iframeRef.current?.contentWindow?.postMessage({ source: 'sage-pres-host', kind: 'print' }, '*');
  }

  /**
   * Request browser fullscreen on the iframe element itself. This is
   * what users want when they say "show me how it would be presented" —
   * the deck takes the whole screen, OS chrome included, and the
   * deck's own keyboard handlers (arrow keys for slide nav, etc.)
   * keep working because the iframe stays focused.
   *
   * Safari prefixes the API as webkitRequestFullscreen and gates
   * exit on Escape automatically. We catch + ignore — the worst case
   * is the user sees nothing happen and uses F11 instead.
   */
  function handleFullscreen(): void {
    const el = iframeRef.current as
      | (HTMLIFrameElement & {
          webkitRequestFullscreen?: () => Promise<void>;
        })
      | null;
    if (!el) return;
    const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (!req) return;
    void req().catch(() => {
      /* user-denied or unsupported */
    });
  }

  return (
    <div className="pres-detail-inner">
      <header className="pres-toolbar">
        <div className="pres-toolbar-lead">
          <h1 className="pres-title">{meta?.name ?? 'Presentation'}</h1>
          {meta?.description && <p className="pres-desc">{meta.description}</p>}
        </div>
        <div className="pres-toolbar-actions">
          <EditButton id={id} />
          <button
            type="button"
            className="btn"
            onClick={handleFullscreen}
            disabled={!blobUrl}
            title="Present full-screen (Esc to exit)"
          >
            Present
          </button>
          <button
            type="button"
            className="btn"
            onClick={handlePrint}
            disabled={!blobUrl}
            title="Print this deck"
          >
            Print
          </button>
          <button
            type="button"
            className={'btn' + (shareOpen ? ' btn--active' : '')}
            onClick={() => setShareOpen((v) => !v)}
            aria-expanded={shareOpen}
            title="Create or manage public share links"
          >
            Share
          </button>
          <button
            type="button"
            className="btn btn--danger-ghost"
            onClick={handleDelete}
            disabled={del.isPending}
            title="Delete this presentation"
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </header>

      {shareOpen && <SharePanel id={id} />}

      <div className="pres-stage">
        {viewLoading && (
          <div className="pres-stage-note" aria-live="polite">
            Loading deck…
          </div>
        )}
        {viewError && (
          <div className="pres-stage-error">
            <div className="pres-stage-error-icon" aria-hidden="true">
              ⚠
            </div>
            <h3>This presentation can't be displayed</h3>
            <p>{viewError}</p>
          </div>
        )}
        {blobUrl && (
          <iframe
            ref={iframeRef}
            title={meta?.name ?? 'Presentation'}
            src={blobUrl}
            className="pres-iframe"
            sandbox={UNTRUSTED_FRAME_SANDBOX_WITH_MODALS}
          />
        )}
      </div>
    </div>
  );
}

function EditButton({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="btn"
      onClick={() => navigate(`/presentations/${id}/edit`)}
      title="Edit metadata or publish a new version"
    >
      Edit
    </button>
  );
}

function SharePanel({ id }: { id: string }): JSX.Element {
  const shares = usePresentationShares(id);
  const mint = useMintPresentationShare();
  const revoke = useRevokePresentationShare();
  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState<{ tokenId: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const forbidden = useMemo(() => {
    const status = (shares.error as (Error & { status?: number }) | null)?.status;
    return status === 403;
  }, [shares.error]);

  function publicLink(token: string): string {
    const base = (
      (import.meta as unknown as { env?: Record<string, string> }).env?.['BASE_URL'] ?? '/'
    ).replace(/\/+$/u, '');
    return `${window.location.origin}${base}/p/${id}/${token}`;
  }

  function handleMint(): void {
    mint.mutate(
      { id, ...(label.trim() ? { label: label.trim() } : {}) },
      {
        onSuccess: (tok) => {
          setMinted({ tokenId: tok.id, url: publicLink(tok.token) });
          setCopied(false);
          setLabel('');
        },
        onError: (e) => window.alert(`Couldn't create a share link: ${(e as Error).message}`),
      },
    );
  }

  async function copy(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  }

  if (forbidden) {
    return (
      <div className="pres-share">
        <p className="pres-share-forbidden">
          You don't have permission to manage share links for this presentation (requires a
          Presentation Admin role).
        </p>
      </div>
    );
  }

  return (
    <div className="pres-share">
      <div className="pres-share-mint">
        <input
          type="text"
          className="pres-share-label"
          placeholder="Label (optional) — e.g. Q2 board"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={handleMint}
          disabled={mint.isPending}
        >
          {mint.isPending ? 'Creating…' : 'Create share link'}
        </button>
      </div>

      {minted && (
        <div className="pres-share-new">
          <div className="pres-share-new-label">
            New link (copy it now — it won't be shown again):
          </div>
          <div className="pres-share-new-row">
            <input
              className="pres-share-url"
              readOnly
              value={minted.url}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="btn btn--sm" onClick={() => void copy(minted.url)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="pres-share-list">
        <div className="pres-share-list-title">Active links</div>
        {shares.isLoading && <div className="pres-share-note">Loading…</div>}
        {shares.data && shares.data.filter((t) => !t.revokedAt).length === 0 && (
          <div className="pres-share-note">No active share links.</div>
        )}
        {shares.data
          ?.filter((t) => !t.revokedAt)
          .map((t) => (
            <div key={t.id} className="pres-share-item">
              <span className="pres-share-item-meta">
                <span className="pres-share-item-prefix">{t.tokenPrefix}…</span>
                {t.label && <span className="pres-share-item-label">{t.label}</span>}
                <span className="pres-share-item-date">
                  {t.expiresAt ? `expires ${formatRelative(t.expiresAt)}` : 'no expiry'}
                </span>
              </span>
              <button
                type="button"
                className="btn-link btn-link--sm btn-link--danger"
                onClick={() => revoke.mutate({ id, tokenId: t.id })}
                disabled={revoke.isPending}
              >
                Revoke
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}

function audienceBadge(a: string): string {
  if (a === 'internal') return 'neutral';
  if (a === 'customer') return 'accent';
  return 'warn';
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function PresStyles(): JSX.Element {
  return (
    <style>{`
      .pres-page {
        display: grid;
        grid-template-columns: 300px 1fr;
        height: calc(100vh - 72px);
        min-height: 560px;
      }
      .pres-list {
        display: flex; flex-direction: column;
        border-right: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        min-height: 0;
      }
      .pres-list-head { padding: var(--space-3) var(--space-3) var(--space-2); }
      .pres-search {
        width: 100%; box-sizing: border-box;
        padding: 7px 10px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        font: inherit; font-size: var(--font-size-sm);
        color: var(--color-text-primary);
      }
      .pres-list-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 0 var(--space-2) var(--space-3); }
      .pres-list-note { padding: var(--space-3); color: var(--color-text-tertiary); font-size: var(--font-size-sm); }
      .pres-list-note--err { color: #b91c1c; }
      .pres-row {
        display: flex; flex-direction: column; gap: 4px;
        width: 100%; text-align: left;
        padding: 9px 10px;
        background: transparent; border: 0; border-radius: var(--radius-sm);
        cursor: pointer; color: var(--color-text-primary);
      }
      .pres-row:hover { background: var(--color-surface-2); }
      .pres-row--active { background: var(--color-accent-softer); }
      .pres-row-name { font-size: var(--font-size-sm); font-weight: var(--font-weight-medium); }
      .pres-row-meta { display: flex; align-items: center; gap: 6px; }
      .pres-row-date { margin-left: auto; font-size: 11px; color: var(--color-text-tertiary); }

      .pres-detail { min-width: 0; min-height: 0; display: flex; background: var(--color-surface-2); }
      .pres-detail-inner { flex: 1; min-width: 0; display: flex; flex-direction: column; }
      .pres-toolbar {
        display: flex; align-items: flex-start; gap: var(--space-3);
        padding: 10px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
      }
      .pres-toolbar-lead { flex: 1; min-width: 0; }
      .pres-title { font-size: var(--font-size-md); font-weight: var(--font-weight-semibold); margin: 0; color: var(--color-text-primary); }
      .pres-desc { margin: 2px 0 0; font-size: var(--font-size-sm); color: var(--color-text-tertiary); }
      .pres-toolbar-actions { display: inline-flex; align-items: center; gap: var(--space-2); flex: none; }
      .btn--active { border-color: var(--color-accent-border); background: var(--color-accent-softer); }
      .btn--danger-ghost { color: #b91c1c; }
      .btn--danger-ghost:hover { background: #fee2e2; border-color: #fecaca; }

      .pres-stage { flex: 1; min-height: 0; position: relative; overflow: hidden; background: var(--color-surface-1); }
      .pres-iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; background: #fff; }
      .pres-stage-note {
        position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
        font-size: var(--font-size-sm); color: var(--color-text-tertiary);
      }
      .pres-stage-error {
        position: absolute; inset: 0;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center; padding: var(--space-6); gap: 6px;
      }
      .pres-stage-error-icon {
        width: 48px; height: 48px; display: inline-flex; align-items: center; justify-content: center;
        font-size: 26px; color: #b91c1c; background: #fee2e2; border-radius: 999px; margin-bottom: var(--space-2);
      }
      .pres-stage-error h3 { margin: 0; font-size: var(--font-size-md); color: var(--color-text-primary); }
      .pres-stage-error p { margin: 0; font-size: var(--font-size-sm); color: var(--color-text-tertiary); max-width: 460px; }

      .pres-empty {
        flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center; padding: var(--space-6); gap: 6px; color: var(--color-text-tertiary);
      }
      .pres-empty-icon {
        width: 52px; height: 52px; display: inline-flex; align-items: center; justify-content: center;
        font-size: 28px; background: var(--color-surface-1); border-radius: 999px; margin-bottom: var(--space-2);
      }
      .pres-empty-title { margin: 0; font-size: var(--font-size-md); font-weight: var(--font-weight-semibold); color: var(--color-text-primary); }
      .pres-empty-sub { margin: 0; font-size: var(--font-size-sm); max-width: 420px; line-height: 1.5; }

      /* Share panel */
      .pres-share {
        padding: var(--space-3) var(--space-4);
        background: var(--color-surface-1);
        border-bottom: 1px solid var(--color-border-subtle);
        display: flex; flex-direction: column; gap: var(--space-3);
      }
      .pres-share-forbidden { margin: 0; font-size: var(--font-size-sm); color: var(--color-text-tertiary); }
      .pres-share-mint { display: flex; gap: var(--space-2); align-items: center; }
      .pres-share-label, .pres-share-url {
        flex: 1; min-width: 0; box-sizing: border-box;
        padding: 6px 10px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        font: inherit; font-size: var(--font-size-sm);
        color: var(--color-text-primary);
      }
      .pres-share-new {
        padding: var(--space-3);
        background: var(--color-accent-softer);
        border: 1px solid var(--color-accent-border);
        border-radius: var(--radius-sm);
        display: flex; flex-direction: column; gap: 6px;
      }
      .pres-share-new-label { font-size: 11px; font-weight: 600; color: var(--color-text-secondary); }
      .pres-share-new-row { display: flex; gap: var(--space-2); }
      .pres-share-url { font-family: ui-monospace, monospace; font-size: 12px; }
      .pres-share-list-title, .pres-share-list .pres-share-note {
        font-size: 10px; font-weight: 650; letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--color-text-tertiary); margin-bottom: 6px;
      }
      .pres-share-note { font-size: var(--font-size-sm); text-transform: none; letter-spacing: 0; font-weight: 400; }
      .pres-share-item {
        display: flex; align-items: center; gap: var(--space-2);
        padding: 6px 0; border-top: 1px solid var(--color-border-subtle);
      }
      .pres-share-item-meta { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
      .pres-share-item-prefix { font-family: ui-monospace, monospace; font-size: 12px; color: var(--color-text-secondary); }
      .pres-share-item-label { font-size: var(--font-size-sm); }
      .pres-share-item-date { margin-left: auto; font-size: 11px; color: var(--color-text-tertiary); }
      .btn-link--danger { color: #b91c1c; }
    `}</style>
  );
}
